import * as Sentry from "@sentry/vue";
import type { ActionContext, GetterTree, Module, MutationTree } from "vuex";

import type { JobAnalysisRequest, JobAnalysisStage } from "@/api";
import type { JobAnalysis } from "@/models/job_analysis";
import { highlightNewPosition } from "@/models/new_position_highlight";
import type { State as RootState } from "@/stores";
import { useAuth } from "@/utils/authentication";

export type JobAnalysisStatus = "queued" | "running" | "failed" | "timed_out";

export type JobAnalysisSource = "ats" | "url" | "file" | "voice";

export interface JobAnalysisEntry {
  id: number;
  source: JobAnalysisSource;
  sourceKey: string;
  companyId: number;
  title: string;
  jobAnalysisId: number;
  status: JobAnalysisStatus;
  stage: JobAnalysisStage;
  errorMessage: string | null;
  updatedAt: string;
  authGeneration: number;
  viewerId: string;
}

export interface ToastItem {
  title: string;
  positionId: number;
}

export interface State {
  entries: JobAnalysisEntry[];
  clock: number;
  toastQueue: ToastItem[];
}

export type StartJobAnalysisPayload =
  | { source: "ats"; komboJobId: string; title: string; companyId: number }
  | { source: "url"; url: string; companyId: number }
  | { source: "file"; file: File; companyId: number }
  | { source: "voice"; retellCallId: string; companyId: number };

type RequestPayload = Exclude<StartJobAnalysisPayload, { source: "file" }>;

const VOICE_TITLE = "New job from your call";

const POLL_INTERVAL_MS = 2_000;
const POLL_REQUEST_TIMEOUT_MS = 15_000;
const POLL_WATCHDOG_MS = 300_000;
const MAX_CONSECUTIVE_POLL_FAILURES = 10;
const SLOW_AFTER_MS = 90_000;

type Context = ActionContext<State, RootState>;

type Poll = {
  timer: ReturnType<typeof setTimeout> | null;
  watchdog: ReturnType<typeof setTimeout> | null;
  abortTimer: ReturnType<typeof setTimeout> | null;
  controller: AbortController | null;
};

// A Poll object is its own generation token: every timer and request callback checks
// `polls.get(entryId) === poll` first, so leftovers of a stopped or restarted poll are no-ops.
const polls = new Map<number, Poll>();
let entrySequence = 0;

const stopPolling = (entryId: number) => {
  const poll = polls.get(entryId);
  if (!poll) return;
  polls.delete(entryId);
  if (poll.timer) clearTimeout(poll.timer);
  if (poll.watchdog) clearTimeout(poll.watchdog);
  if (poll.abortTimer) clearTimeout(poll.abortTimer);
  poll.controller?.abort();
};

const findEntry = (
  ctx: Context,
  entryId: number,
): JobAnalysisEntry | undefined =>
  ctx.state.entries.find((entry) => entry.id === entryId);

// Undefined once this poll must not touch the store any more: it was replaced or stopped, its entry
// was dismissed or wiped by a tenant reset, or the session changed since the entry was created.
const liveEntry = (
  ctx: Context,
  entryId: number,
  poll: Poll,
): JobAnalysisEntry | undefined => {
  if (polls.get(entryId) !== poll) return undefined;
  const entry = findEntry(ctx, entryId);
  if (entry && useAuth().getGeneration() === entry.authGeneration) return entry;
  stopPolling(entryId);
  return undefined;
};

const isInFlight = (entry: JobAnalysisEntry): boolean =>
  entry.status === "queued" || entry.status === "running";

const settleSucceeded = (
  ctx: Context,
  entry: JobAnalysisEntry,
  positionId: number,
) => {
  stopPolling(entry.id);
  highlightNewPosition(entry.viewerId, positionId);
  ctx.commit("removeEntry", entry.id);
  ctx.commit("pushToast", { title: entry.title, positionId });
  // The toast is already queued, so a failed refresh must not hide the result.
  ctx
    .dispatch("position/getPositions", { refresh: true }, { root: true })
    .catch(() => undefined);
  if (entry.source === "ats") {
    ctx
      .dispatch("jobState/getJobStates", null, { root: true })
      .catch(() => undefined);
  }
};

const settleStopped = (
  ctx: Context,
  entryId: number,
  poll: Poll,
  status: "failed" | "timed_out",
) => {
  if (!liveEntry(ctx, entryId, poll)) return;
  stopPolling(entryId);
  ctx.commit("updateEntry", { id: entryId, patch: { status } });
};

const pollOnce = async (
  ctx: Context,
  entryId: number,
  poll: Poll,
  jobAnalysisId: number,
  consecutiveFailures: number,
) => {
  if (!liveEntry(ctx, entryId, poll)) return;
  ctx.commit("setClock", Date.now());

  // Each request closes over its own controller and timer so a stale request can only clear its own.
  const controller = new AbortController();
  const abortTimer = setTimeout(
    () => controller.abort(),
    POLL_REQUEST_TIMEOUT_MS,
  );
  poll.controller = controller;
  poll.abortTimer = abortTimer;
  const clearOwnAbort = () => {
    clearTimeout(abortTimer);
    if (poll.abortTimer === abortTimer) poll.abortTimer = null;
    if (poll.controller === controller) poll.controller = null;
  };

  try {
    const analysis: JobAnalysis = await ctx.dispatch(
      "jobAnalysis/getJobAnalysis",
      { jobAnalysisId, signal: controller.signal },
      { root: true },
    );
    clearOwnAbort();
    const entry = liveEntry(ctx, entryId, poll);
    if (!entry) return;

    if (analysis.position_id !== null) {
      settleSucceeded(ctx, entry, analysis.position_id);
      return;
    }
    if (analysis.error_message !== null) {
      ctx.commit("updateEntry", {
        id: entryId,
        patch: {
          stage: analysis.stage,
          errorMessage: analysis.error_message,
          updatedAt: analysis.updated_at,
        },
      });
      settleStopped(ctx, entryId, poll, "failed");
      return;
    }

    ctx.commit("updateEntry", {
      id: entryId,
      patch: {
        status: "running",
        stage: analysis.stage,
        errorMessage: null,
        updatedAt: analysis.updated_at,
      },
    });
    poll.timer = setTimeout(
      () => pollOnce(ctx, entryId, poll, jobAnalysisId, 0),
      POLL_INTERVAL_MS,
    );
  } catch {
    clearOwnAbort();
    if (!liveEntry(ctx, entryId, poll)) return;
    const failures = consecutiveFailures + 1;
    if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
      settleStopped(ctx, entryId, poll, "timed_out");
      return;
    }
    poll.timer = setTimeout(
      () => pollOnce(ctx, entryId, poll, jobAnalysisId, failures),
      POLL_INTERVAL_MS,
    );
  }
};

const startPolling = (ctx: Context, entryId: number, jobAnalysisId: number) => {
  stopPolling(entryId);
  const poll: Poll = {
    timer: null,
    watchdog: null,
    abortTimer: null,
    controller: null,
  };
  polls.set(entryId, poll);
  poll.watchdog = setTimeout(
    () => settleStopped(ctx, entryId, poll, "timed_out"),
    POLL_WATCHDOG_MS,
  );
  void pollOnce(ctx, entryId, poll, jobAnalysisId, 0);
};

const requestKeyOf = (payload: RequestPayload): string =>
  payload.source === "ats"
    ? payload.komboJobId
    : payload.source === "url"
      ? payload.url
      : payload.retellCallId;

const titleFor = (payload: StartJobAnalysisPayload): string => {
  switch (payload.source) {
    case "ats":
      return payload.title;
    case "url":
      // The server accepts any URL with a scheme and host, which is looser than the WHATWG parser.
      return URL.canParse(payload.url)
        ? new URL(payload.url).hostname
        : payload.url;
    case "file":
      return payload.file.name;
    case "voice":
      return VOICE_TITLE;
  }
};

const requestFor = (payload: RequestPayload): JobAnalysisRequest => {
  switch (payload.source) {
    case "ats":
      return {
        company_id: payload.companyId,
        kombo_job_id: payload.komboJobId,
      };
    case "url":
      return {
        company_id: payload.companyId,
        job_description_url: payload.url,
      };
    case "voice":
      return {
        company_id: payload.companyId,
        retell_call_id: payload.retellCallId,
      };
  }
};

const postAnalysis = async (
  ctx: Context,
  payload: StartJobAnalysisPayload,
): Promise<JobAnalysis> => {
  if (payload.source === "file") {
    return ctx.dispatch(
      "jobAnalysis/postJobAnalysisUpload",
      { companyId: payload.companyId, file: payload.file },
      { root: true },
    );
  }
  // Wait for the analysis ID instead of aborting a potentially committed start.
  return ctx.dispatch(
    "jobAnalysis/postJobAnalysis",
    { request: requestFor(payload) },
    { root: true },
  );
};

const sourceKeyAfterPost = (
  payload: StartJobAnalysisPayload,
  analysis: JobAnalysis,
): string => {
  if (payload.source !== "file") return requestKeyOf(payload);
  if (analysis.file_hash === null) {
    throw new Error("Upload response has no file_hash");
  }
  return analysis.file_hash;
};

const getters: GetterTree<State, RootState> = {
  orderedEntries: (state): JobAnalysisEntry[] => [...state.entries].reverse(),
  isInFlight:
    (state) =>
    (source: JobAnalysisSource, key: string, companyId: number): boolean =>
      state.entries.some(
        (entry) =>
          entry.source === source &&
          entry.sourceKey === key &&
          entry.companyId === companyId &&
          isInFlight(entry),
      ),
  slowEntryIds: (state): number[] =>
    state.entries
      .filter(
        (entry) =>
          entry.status === "running" &&
          state.clock - new Date(entry.updatedAt).getTime() > SLOW_AFTER_MS,
      )
      .map((entry) => entry.id),
  toastQueue: (state): ToastItem[] => state.toastQueue,
};

const mutations: MutationTree<State> = {
  addEntry(state, entry: JobAnalysisEntry) {
    state.entries.push(entry);
  },
  updateEntry(
    state,
    { id, patch }: { id: number; patch: Partial<JobAnalysisEntry> },
  ) {
    const entry = state.entries.find((candidate) => candidate.id === id);
    if (entry) Object.assign(entry, patch);
  },
  removeEntry(state, id: number) {
    state.entries = state.entries.filter((entry) => entry.id !== id);
  },
  setClock(state, now: number) {
    state.clock = now;
  },
  pushToast(state, toast: ToastItem) {
    state.toastQueue.push(toast);
  },
  shiftToast(state) {
    state.toastQueue.shift();
  },
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: (): State => ({
    entries: [],
    clock: 0,
    toastQueue: [],
  }),
  getters,
  mutations,
  actions: {
    async start(ctx, payload: StartJobAnalysisPayload): Promise<void> {
      if (payload.source !== "file") {
        const existing = ctx.state.entries.find(
          (entry) =>
            entry.source === payload.source &&
            entry.sourceKey === requestKeyOf(payload) &&
            entry.companyId === payload.companyId &&
            entry.status !== "failed",
        );
        if (existing) {
          if (existing.status === "timed_out")
            await ctx.dispatch("resume", existing.id);
          return;
        }
      }

      const title = titleFor(payload);
      const viewerId = useAuth().user.value?.sub;
      if (viewerId === undefined) {
        const error = new Error(
          "Job analysis started without a signed-in user",
        );
        Sentry.captureException(error);
        throw error;
      }
      const analysis = await postAnalysis(ctx, payload);
      ctx.commit("setClock", Date.now());

      const progress = {
        status: "running" as const,
        stage: analysis.stage,
        errorMessage: null,
        updatedAt: analysis.updated_at,
      };
      // Resume polling if a response refers to an analysis already tracked locally.
      const existing = ctx.state.entries.find(
        (entry) => entry.jobAnalysisId === analysis.id,
      );
      if (existing) {
        ctx.commit("updateEntry", { id: existing.id, patch: progress });
        startPolling(ctx, existing.id, analysis.id);
        return;
      }

      entrySequence += 1;
      ctx.commit("addEntry", {
        id: entrySequence,
        source: payload.source,
        sourceKey: sourceKeyAfterPost(payload, analysis),
        companyId: payload.companyId,
        title,
        jobAnalysisId: analysis.id,
        status: "queued",
        stage: analysis.stage,
        errorMessage: null,
        updatedAt: analysis.updated_at,
        authGeneration: useAuth().getGeneration(),
        viewerId,
      } satisfies JobAnalysisEntry);
      startPolling(ctx, entrySequence, analysis.id);
    },
    resume(ctx, entryId: number) {
      const entry = findEntry(ctx, entryId);
      if (
        !entry ||
        entry.status !== "timed_out" ||
        entry.authGeneration !== useAuth().getGeneration()
      )
        return;
      ctx.commit("updateEntry", { id: entryId, patch: { status: "running" } });
      startPolling(ctx, entryId, entry.jobAnalysisId);
    },
    dismiss(ctx, entryId: number) {
      stopPolling(entryId);
      ctx.commit("removeEntry", entryId);
    },
  },
};
